import {
  Box,
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
} from '@chakra-ui/react';
import React, { useState } from 'react';
import { v4 as uuidV4 } from 'uuid';
import { utils, writeFile } from 'xlsx';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';

import { AggregatedLimitedPartnerCommitmentDataGQLType } from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { Icon } from 'packages/common-frontend/components/icons';

const formatExportRow = (
  row: AggregatedLimitedPartnerCommitmentDataGQLType,
): Record<string, string | number | null | undefined> => ({
  Name: row.limitedPartnerName,
  'Number of LP Commitments': row.commitmentCount,
  'Total Committed Capital': commonUtils.numberToCurrencyWithSymbol({
    number: row.totalCommittedCapital,
  }),
  '% of Portfolio Committed Capital': commonUtils.numberToFormattedString(
    row.percentageOfPortfolioCommittedCapital,
    'percentage',
    false,
  ),
  'Uncalled Capital': commonUtils.numberToCurrencyWithSymbol({
    number: row.totalUncalledCapital,
  }),
  '% of Portfolio Uncalled Capital': commonUtils.numberToFormattedString(
    row.percentageOfPortfolioUncalledCapital,
    'percentage',
    false,
  ),
  'Recallable Capital': commonUtils.numberToCurrencyWithSymbol({
    number: row.totalRecallableCapital,
  }),
  'Eligible Uncalled Capital': commonUtils.numberToCurrencyWithSymbol({
    number: row.eligibleUncalledCapital,
  }),
  'Eligible Uncalled Capital Less Kickouts': commonUtils.numberToCurrencyWithSymbol(
    {
      number: row.eligibleUncalledCapitalLessKickouts,
    },
  ),
  'Advance Rate': commonUtils.numberToFormattedString(
    row.advanceRate,
    'percentage',
    false,
  ),
  'Net Uncalled Capital': commonUtils.numberToCurrencyWithSymbol({
    number: row.netUncalledCapital,
  }),
  '% of Portfolio Net Uncalled Capital': commonUtils.numberToFormattedString(
    row.percentageOfPortfolioNetUncalledCapital,
    'percentage',
    false,
  ),
});

type LimitedPartnersExportButtonProps = {
  data: AggregatedLimitedPartnerCommitmentDataGQLType[];
};

export const LimitedPartnersExportButton = ({
  data,
}: LimitedPartnersExportButtonProps): React.ReactElement => {
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = (format: 'csv' | 'xlsx'): void => {
    setIsExporting(true);
    try {
      const rows = data.map(formatExportRow);
      const ws = utils.json_to_sheet(rows);

      const filenamePrefix = `limited-partners-export-${uuidV4().slice(-12)}`;
      const todayStr = commonDate.currentDateStr();

      if (format === 'csv') {
        const csv = utils.sheet_to_csv(ws);
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${filenamePrefix}-${todayStr}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      } else {
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, 'Limited Partners');
        writeFile(wb, `${filenamePrefix}-${todayStr}.xlsx`);
      }
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Box style={{ alignSelf: 'self-end' }}>
      <Menu>
        <MenuButton
          as={Button}
          colorScheme="gray"
          size="sm"
          variant="secondary-muted"
          isLoading={isExporting}
          leftIcon={<Icon icon="arrow-down-to-bracket" />}
        >
          Export
        </MenuButton>
        <MenuList zIndex={100}>
          <MenuItem onClick={() => handleExport('csv')}>Download CSV</MenuItem>
          <MenuItem onClick={() => handleExport('xlsx')}>
            Download XLSX
          </MenuItem>
        </MenuList>
      </Menu>
    </Box>
  );
};
